/**
 * Location Selector Popup
 * Detects user's location and shows nearby store options
 */

class LocationSelectorPopup {
  constructor() {
    this.popup = document.getElementById('location-selector-popup');
    this.locationList = document.getElementById('location-list');
    this.loadingElement = document.getElementById('location-loading');
    this.closeBtn = this.popup?.querySelector('.location-popup-close');
    this.continueBtn = document.getElementById('location-continue-btn');
    
    this.stores = [];
    this.userLocation = null;
    this.selectedStore = null;
    
    this.config = window.storeLocations || {};
    
    if (this.popup) {
      this.init();
    }
  }

  init() {
    // Check if user has already selected a location or dismissed popup
    const selectedLocation = localStorage.getItem(this.config.storageKey);
    const popupShown = sessionStorage.getItem(this.config.popupShownKey);
    
    if (selectedLocation) {
      console.log('User has already selected a location:', selectedLocation);
      return; // Don't show popup if location already selected
    }
    
    if (popupShown) {
      console.log('Popup already shown this session');
      return; // Don't show popup again in same session
    }
    
    // Load stores and show popup
    this.loadStores().then(() => {
      if (this.config.autoDetectLocation) {
        this.getUserLocation();
      } else {
        this.displayStores();
      }
      
      // Show popup after delay
      setTimeout(() => {
        this.showPopup();
      }, this.config.showPopupDelay || 1000);
    });
    
    // Event listeners
    this.closeBtn?.addEventListener('click', () => this.hidePopup());
    this.continueBtn?.addEventListener('click', () => this.hidePopup());
    
    // Close on overlay click
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.hidePopup();
      }
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.hidePopup();
      }
    });
  }

  async loadStores() {
    try {
      // Try to load from API endpoint
      const apiEndpoint = this.config.apiEndpoint || 'http://localhost:3002';
      const response = await fetch(`${apiEndpoint}/api/locations`);
      
      if (!response.ok) {
        throw new Error('API not available');
      }
      
      const data = await response.json();
      this.stores = data.locations || [];
    } catch (error) {
      console.warn('Could not load stores from API, using fallback data:', error);
      // Fallback to hardcoded store data
      this.stores = this.getFallbackStores();
    }
  }

  getFallbackStores() {
    // Fallback store data embedded in the script
    return [
      {
        key: 'franchise-a',
        name: 'Omaha',
        address: '1201 S 157th Street',
        city: 'Omaha',
        state: 'Nebraska',
        zip: '68130',
        coordinates: { lat: 41.2545, lng: -96.1290 },
        store_url: 'mainstream-omaha.myshopify.com'
      },
      {
        key: 'northfield',
        name: 'Northfield',
        address: '506 Division St S',
        city: 'Northfield',
        state: 'Minnesota',
        zip: '55057',
        coordinates: { lat: 44.4583, lng: -93.1616 },
        store_url: 'fkn403-pw.myshopify.com'
      },
      {
        key: 'champaign',
        name: 'Champaign',
        address: '1741 West Kirby Avenue',
        city: 'Champaign',
        state: 'Illinois',
        zip: '61821',
        coordinates: { lat: 40.1261, lng: -88.2871 },
        store_url: 'mxkeuj-wx.myshopify.com'
      },
      {
        key: 'woodbury',
        name: 'Mainstream Boutique Woodbury Mendota Heights',
        address: '720 Main Street Suite 108',
        city: 'Mendota Heights',
        state: 'Minnesota',
        zip: '55118',
        coordinates: { lat: 44.8855, lng: -93.1416 },
        store_url: 'vhxrc8-6w.myshopify.com'
      },
      {
        key: 'stillwater',
        name: 'Mainstream Boutique Stillwater',
        address: '5805 Neal Avenue North',
        city: 'Stillwater',
        state: 'Minnesota',
        zip: '55082',
        coordinates: { lat: 45.0616, lng: -92.8063 },
        store_url: 'mbstillwater.myshopify.com'
      },
      {
        key: 'janesville',
        name: 'Mainstream Boutique Janesville & Sheboygan',
        address: '3023 Milton Ave',
        city: 'Janesville',
        state: 'Wisconsin',
        zip: '53545',
        coordinates: { lat: 42.6806, lng: -89.0187 },
        store_url: 'mainstream-janesville.myshopify.com'
      },
      {
        key: 'ankeny',
        name: 'Mainstream Boutique Ankeny',
        address: '1450 SW Vintage Pkwy',
        city: 'Ankeny',
        state: 'Iowa',
        zip: '50023',
        coordinates: { lat: 41.7275, lng: -93.6055 },
        store_url: 'mainstreamboutique-ankeny.myshopify.com'
      },
      {
        key: 'fargo',
        name: 'Mainstream Boutique-Fargo',
        address: '4600 32nd Ave S',
        city: 'Fargo',
        state: 'North Dakota',
        zip: '58104',
        coordinates: { lat: 46.8447, lng: -96.8595 },
        store_url: 'mainstream-fargo.myshopify.com'
      },
      {
        key: 'charlotte',
        name: 'Mainstream Boutique Charlotte',
        address: '7314 Waverly Walk Avenue',
        city: 'Charlotte',
        state: 'North Carolina',
        zip: '28277',
        coordinates: { lat: 35.0905, lng: -80.8466 },
        store_url: 'mainstream-charlotte.myshopify.com'
      },
      {
        key: 'cranberry',
        name: 'Mainstream Boutique Cranberry',
        address: 'Cranberry Township',
        city: 'Cranberry',
        state: 'Pennsylvania',
        zip: '16066',
        coordinates: { lat: 40.6848, lng: -80.1073 },
        store_url: 'mainstream-cranberry.myshopify.com'
      },
      {
        key: 'oxford',
        name: 'Mainstream Boutique - Oxford',
        address: 'Oxford',
        city: 'Oxford',
        state: 'Mississippi',
        zip: '38655',
        coordinates: { lat: 34.3665, lng: -89.5192 },
        store_url: 'n1ytgh-s9.myshopify.com'
      },
      {
        key: 'spring',
        name: 'Mainstream Boutique Spring',
        address: 'Spring',
        city: 'Spring',
        state: 'Texas',
        zip: '77373',
        coordinates: { lat: 30.0799, lng: -95.4171 },
        store_url: 'mainstreamspring.myshopify.com'
      },
      {
        key: 'willmar',
        name: 'Mainstream Boutique Willmar',
        address: 'Willmar',
        city: 'Willmar',
        state: 'Minnesota',
        zip: '56201',
        coordinates: { lat: 45.1219, lng: -95.0433 },
        store_url: '2fe8d7-17.myshopify.com'
      },
      {
        key: 'bloomington',
        name: 'MainstreamBoutique-Bloomington',
        address: 'Bloomington',
        city: 'Bloomington',
        state: 'Minnesota',
        zip: '55420',
        coordinates: { lat: 44.8408, lng: -93.2983 },
        store_url: 'mainstreamboutique-bloomington.myshopify.com'
      },
      {
        key: 'woodlands',
        name: 'Mainstream Boutique The Woodlands',
        address: 'The Woodlands',
        city: 'The Woodlands',
        state: 'Texas',
        zip: '77380',
        coordinates: { lat: 30.1658, lng: -95.4613 },
        store_url: 'swhsr3-kb.myshopify.com'
      },
      {
        key: 'middleton',
        name: 'Mainstream Boutique Middleton',
        address: 'Middleton',
        city: 'Middleton',
        state: 'Wisconsin',
        zip: '53562',
        coordinates: { lat: 43.0972, lng: -89.5043 },
        store_url: 'v6dmje-c2.myshopify.com'
      },
      {
        key: 'castlepines',
        name: 'Mainstream Boutique Castle Pines',
        address: 'Castle Pines',
        city: 'Castle Pines',
        state: 'Colorado',
        zip: '80108',
        coordinates: { lat: 39.4561, lng: -104.8861 },
        store_url: '11b8c4-a9.myshopify.com'
      },
      {
        key: 'mankato',
        name: 'Mainstream Boutique - Mankato',
        address: 'Mankato',
        city: 'Mankato',
        state: 'Minnesota',
        zip: '56001',
        coordinates: { lat: 44.1636, lng: -94.0003 },
        store_url: 'gkksfb-pj.myshopify.com'
      },
      {
        key: 'macandme',
        name: 'Mac and Me',
        address: 'Mac and Me',
        city: 'Charlotte',
        state: 'North Carolina',
        zip: '28277',
        coordinates: { lat: 35.0905, lng: -80.8466 },
        store_url: 'macandme-6984.myshopify.com'
      },
      {
        key: 'goldenvalley',
        name: 'Mainstream-GoldenValley',
        address: 'Golden Valley',
        city: 'Golden Valley',
        state: 'Minnesota',
        zip: '55422',
        coordinates: { lat: 44.9889, lng: -93.3502 },
        store_url: 'mainstream-goldenvalley.myshopify.com'
      },
      {
        key: 'home-office',
        name: 'Mainstream Boutique Home Office',
        address: 'Home Office',
        city: 'Minneapolis',
        state: 'Minnesota',
        zip: '55401',
        coordinates: { lat: 44.9778, lng: -93.2650 },
        store_url: 'mainstream-boutique-development-store.myshopify.com'
      }
    ];
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      this.displayStores();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        console.log('User location detected:', this.userLocation);
        this.calculateDistances();
        this.displayStores();
      },
      (error) => {
        console.warn('Could not get user location:', error.message);
        this.displayStores();
      },
      {
        timeout: 5000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }

  calculateDistances() {
    if (!this.userLocation) return;

    this.stores.forEach(store => {
      if (store.coordinates) {
        store.distance = this.calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          store.coordinates.lat,
          store.coordinates.lng
        );
      }
    });

    // Sort by distance
    this.stores.sort((a, b) => {
      if (a.distance === undefined) return 1;
      if (b.distance === undefined) return -1;
      return a.distance - b.distance;
    });
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    // Haversine formula for calculating distance between coordinates
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return Math.round(distance);
  }

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  displayStores() {
    this.loadingElement.style.display = 'none';
    this.locationList.style.display = 'block';

    const storesHTML = this.stores.map((store, index) => {
      const isClosest = index === 0 && store.distance !== undefined;
      const distanceText = store.distance !== undefined 
        ? `<span class="location-distance">${store.distance} miles away</span>`
        : '';

      return `
        <div class="location-item ${isClosest ? 'closest-location' : ''}" data-store-key="${store.key}">
          ${isClosest ? '<span class="closest-badge">Closest to You</span>' : ''}
          <div class="location-info">
            <h3 class="location-name">${store.name}</h3>
            <p class="location-address">
              ${store.address ? store.address + '<br>' : ''}
              ${store.city}, ${store.state} ${store.zip}
            </p>
            ${distanceText}
          </div>
          <button class="location-select-btn" data-store-key="${store.key}" data-store-url="${store.store_url}">
            Shop This Location
          </button>
        </div>
      `;
    }).join('');

    this.locationList.innerHTML = storesHTML;

    // Add click handlers to select buttons
    this.locationList.querySelectorAll('.location-select-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const storeKey = e.target.dataset.storeKey;
        const storeUrl = e.target.dataset.storeUrl;
        this.selectStore(storeKey, storeUrl);
      });
    });
  }

  selectStore(storeKey, storeUrl) {
    // Save selection to localStorage
    const storeData = {
      key: storeKey,
      url: storeUrl,
      selectedAt: new Date().toISOString()
    };
    
    localStorage.setItem(this.config.storageKey, JSON.stringify(storeData));
    
    console.log('Store selected:', storeData);
    
    // Open the selected store in a new tab
    window.open(`https://${storeUrl}`, '_blank');
    
    // Hide the popup
    this.hidePopup();
  }

  showPopup() {
    this.popup.classList.add('active');
    document.body.style.overflow = 'hidden'; // Prevent scrolling
    sessionStorage.setItem(this.config.popupShownKey, 'true');
  }

  hidePopup() {
    this.popup.classList.remove('active');
    document.body.style.overflow = ''; // Restore scrolling
    sessionStorage.setItem(this.config.popupShownKey, 'true');
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LocationSelectorPopup();
  });
} else {
  new LocationSelectorPopup();
}
